import { useState, useEffect, useRef, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { RruleBuilderFieldDef } from '../types';
import CustomSelectInput from '../../CustomSelectInput/CustomSelectInput';
import CustomDatePicker from '../../CustomDatePicker/CustomDatePicker';
import CustomTextInput from '../../CustomInput/CustomTextInput';
import Typhography from '../../Typhography/Typhography';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const FREQ_OPTIONS = [
  { label: 'Giornaliero', value: 'DAILY' },
  { label: 'Settimanale', value: 'WEEKLY' },
  { label: 'Mensile', value: 'MONTHLY' },
];

const WEEKDAY_OPTIONS = [
  { label: 'L', value: 'MO' },
  { label: 'M', value: 'TU' },
  { label: 'M', value: 'WE' },
  { label: 'G', value: 'TH' },
  { label: 'V', value: 'FR' },
  { label: 'S', value: 'SA' },
  { label: 'D', value: 'SU' },
];

const END_OPTIONS: { label: string; value: EndCondition }[] = [
  { label: 'Dopo N occorrenze', value: 'count' },
  { label: 'Termina il', value: 'until' },
];

type EndCondition = 'count' | 'until';

const buildRrule = (
  freq: string,
  weekdays: string[],
  endCondition: EndCondition,
  count: number,
  until: Date | null
): string => {
  let rrule = `FREQ=${freq}`;

  if (freq === 'WEEKLY' && weekdays.length > 0) {
    rrule += `;BYDAY=${weekdays.join(',')}`;
  }

  if (endCondition === 'count') {
    rrule += `;COUNT=${count}`;
  } else if (endCondition === 'until' && until) {
    // Converti in UTC prima di formattare: evita che orario locale CEST (UTC+2)
    // venga scritto con la Z finale come fosse UTC (off-by-one sull'ultima occorrenza).
    const untilStr = dayjs(until).utc().format('YYYYMMDDTHHmmss') + 'Z';
    rrule += `;UNTIL=${untilStr}`;
  }

  return rrule;
};

const sectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #e7e7e7',
  borderRadius: 10,
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  backgroundColor: '#fbfbfb',
};

const dividerStyle: React.CSSProperties = {
  borderTop: '1px solid #e7e7e7',
  margin: '0 -16px',
};

const parseRrule = (rrule: string) => {
  const str = rrule.replace(/^RRULE:/, '');
  const parts: Record<string, string> = {};
  str.split(';').forEach((part) => {
    const [key, val] = part.split('=');
    if (key && val !== undefined) parts[key] = val;
  });
  return parts;
};

const RruleBuilderField = ({ field }: { field: RruleBuilderFieldDef }) => {
  const { control, setValue } = useFormContext();
  const currentValue = useWatch({ control, name: field.key });
  const lastSetValue = useRef<string | null>(null);

  const [freq, setFreq] = useState<string>('WEEKLY');
  const [weekdays, setWeekdays] = useState<string[]>(['MO']);
  const [endCondition, setEndCondition] = useState<EndCondition>('count');
  const [count, setCount] = useState<number>(10);
  const [until, setUntil] = useState<Date | null>(null);

  // Sync local state when form value is set externally (e.g. reset() on edit)
  useEffect(() => {
    if (!currentValue) return;
    // Se il valore corrente è uguale a quello che abbiamo settato noi, ignora
    if (currentValue === lastSetValue.current) return;
    // Cambiamento esterno (form reset/edit) → parsa e sincronizza stato locale
    const parsed = parseRrule(currentValue);
    if (parsed.FREQ) setFreq(parsed.FREQ);
    if (parsed.BYDAY) {
      setWeekdays(parsed.BYDAY.split(','));
    } else {
      setWeekdays([]);
    }
    if (parsed.COUNT) {
      setCount(parseInt(parsed.COUNT, 10));
      setEndCondition('count');
    } else if (parsed.UNTIL) {
      setUntil(dayjs(parsed.UNTIL.replace('Z', ''), 'YYYYMMDDTHHmmss').toDate());
      setEndCondition('until');
    }
    lastSetValue.current = currentValue;
  }, [currentValue]);

  // Rebuild rrule whenever any local state changes and push to form
  useEffect(() => {
    const rrule = buildRrule(freq, weekdays, endCondition, count, until);
    lastSetValue.current = rrule;
    setValue(field.key, rrule, { shouldDirty: true });
  }, [freq, weekdays, endCondition, count, until, field.key, setValue]);

  const onWeekdayToggle = useCallback((day: string) => {
    setWeekdays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  }, []);

  return (
    <div className="d-flex flex-column gap-2">
      {field.label && (
        <Typhography size="bold-14" type="span" className="label-input">
          {field.label}
        </Typhography>
      )}

      <div style={cardStyle}>
        {/* Frequency */}
        <div style={sectionStyle}>
          <CustomSelectInput
            label="Frequenza"
            value={freq}
            options={FREQ_OPTIONS}
            onChange={(val: string) => setFreq(val)}
            placeholder="Seleziona frequenza"
          />
        </div>

        {/* Weekdays (only if WEEKLY) */}
        {freq === 'WEEKLY' && (
          <>
            <div style={dividerStyle} />
            <div style={sectionStyle}>
              <Typhography size="bold-14" type="span" className="label-input">
                Giorni della settimana
              </Typhography>
              <div style={{ display: 'flex', gap: 8 }}>
                {WEEKDAY_OPTIONS.map((day) => {
                  const active = weekdays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => onWeekdayToggle(day.value)}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: active ? '2px solid #ffd15a' : '1.5px solid #e7e7e7',
                        backgroundColor: active ? '#ffd15a' : '#ffffff',
                        color: active ? '#03212c' : '#5a5a5a',
                        fontWeight: active ? 700 : 500,
                        fontSize: 13,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s ease',
                        flexShrink: 0,
                      }}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* End condition */}
        <div style={dividerStyle} />
        <div style={sectionStyle}>
          <Typhography size="bold-14" type="span" className="label-input">
            Fine ricorrenza
          </Typhography>

          {/* Toggle buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            {END_OPTIONS.map((opt) => {
              const active = endCondition === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setEndCondition(opt.value)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: active ? '2px solid #ffd15a' : '1.5px solid #e7e7e7',
                    backgroundColor: active ? '#ffd15a' : '#ffffff',
                    color: active ? '#03212c' : '#5a5a5a',
                    fontWeight: active ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Sub-input */}
          {endCondition === 'count' && (
            <div style={{ maxWidth: 140 }}>
              <CustomTextInput
                type="number"
                value={count}
                min={1}
                onValueNumberChange={({ valueAsNumber }) => setCount(valueAsNumber)}
              />
            </div>
          )}
          {endCondition === 'until' && (
            <CustomDatePicker
              date={until}
              onChange={(val: Date | null) => setUntil(val)}
              showIcon={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RruleBuilderField;
